import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  ChevronDown,
  Clock,
  DollarSign,
  Home,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import { useState } from "react";
import { Col, Container, Row } from "react-bootstrap";

const brandRed = "#b22335";
const brandNavy = "#3D3B6D";

const faqStyles = {
  section: {
    backgroundColor: "white",
    padding: "30px 0",
  },
  subtitleWrap: {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: "#ffffff",
    color: "#2b4d8d",
    fontFamily: "'hauora-medium', sans-serif",
    fontSize: "15px",
    padding: "8px 25px",
    borderRadius: "50px",
    letterSpacing: "0.3px",
    boxShadow: "0 0 20px rgba(178, 35, 53, 0.25)",
    border: "1px solid rgba(178, 35, 53, 0.1)",
  },
  dot: {
    height: "8px",
    width: "8px",
    backgroundColor: brandRed,
    borderRadius: "50%",
    display: "inline-block",
    marginRight: "12px",
    flexShrink: 0,
  },
  heading: {
    fontFamily: "'hauora-medium', sans-serif",
    color: brandNavy,
  },
  headingRed: {
    color: brandRed,
  },
  questionText: {
    fontFamily: "'hauora-medium', sans-serif",
    fontSize: "clamp(1rem, calc(1rem + 0.55vw), 1rem)",
    color: brandNavy,
  },
  answerText: {
    fontFamily: "'hauora-medium', sans-serif",
    fontSize: "15px",
    color: "#4d5663",
    padding: "0 24px 24px 64px",
  },
  btnBase: {
    width: "100%",
    padding: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "none",
    border: "none",
    textAlign: "left",
    cursor: "pointer",
  },
};

/* Responsive heading via CSS injection — avoids a separate file */
const ResponsiveStyle = () => (
  <style>{`
    
    .faq-section-wrap {
      background-color: white;
      padding: 30px 10px;
    }
    @media (max-width: 991px) {
      .faq-section-wrap { padding: 20px 10px; }
    }
    @media (max-width: 576px) {
      .faq-section-wrap { padding: 20px 10px; }
      .faq-answer-text  { padding: 0 16px 20px 16px !important; font-size: 15px !important; }
      .faq-question-btn { padding: 16px !important; }
      .faq-subtitle     { font-size: 13px !important; padding: 7px 18px !important; }
    }
  `}</style>
);

const Faq = () => {
  const [activeId, setActiveId] = useState(null);

  const faqs = [
    {
      id: 1,
      icon: <Home size={20} />,
      question: "Do you provide free roof inspections?",
      answer:
        "Yes, we offer free roof inspections for homeowners in Dublin, Ohio. Our experts check for leaks, storm damage, missing shingles, and structural issues. After inspection, we provide a detailed report and honest recommendations. There is no obligation to hire us after inspection. Our goal is to help homeowners understand their roof condition and avoid costly repairs in the future. We focus on transparency, safety, and long-term roofing solutions for every property.",
    },
    {
      id: 2,
      icon: <ShieldCheck size={20} />,
      question:
        "How do I find the best roofing contractor near me in Dublin, Ohio?",
      answer:
        "To find the best roofing contractor, check experience, reviews, licensing, and completed projects. A reliable contractor should offer free inspections, clear pricing, and strong warranties. Look for companies with local Ohio experience since weather conditions affect roofing performance. Always choose a contractor with proven customer satisfaction and emergency support. Ultimates Roofing provides trusted services with experienced professionals and high-quality materials tailored for Dublin, Ohio homes.",
    },
    {
      id: 3,
      icon: <Clock size={20} />,
      question: "What roofing materials are best for Ohio weather?",
      answer: (
        <>
          Asphalt shingles, metal roofing, and rubber-based materials like EPDM
          are best for{" "}
          <a
            href="https://en.wikipedia.org/wiki/Dublin,_Ohio"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: brandRed,
              textDecoration: "none",
              fontWeight: "500",
            }}
          >
            Dublin, Ohio
          </a>{" "}
          weather. These materials resist heavy snow, rain, wind, and
          temperature changes. Metal roofing is highly durable, while shingles
          are cost-effective and reliable. EPDM works well for flat roofs.
          Choosing the right material depends on budget, structure, and
          long-term durability needs.
        </>
      ),
    },
    {
      id: 4,
      icon: <AlertTriangle size={20} />,
      question: "How do I know if I need a roof repair or replacement?",
      answer:
        "If your roof has leaks, missing shingles, sagging areas, or repeated damage, you may need repairs or replacement. Minor issues can often be repaired, but older roofs (15–25 years) usually need replacement. A professional inspection helps determine the best solution based on damage level and roof condition.",
    },
    {
      id: 5,
      icon: <Clock size={20} />,
      question: "How long does a roof installation take?",
      answer:
        "Most residential roof installations take 1 to 3 days, depending on size, weather, and material type. Larger or more complex roofs may take longer. Our team ensures fast, efficient installation without compromising quality or safety standards.",
    },
    {
      id: 6,
      icon: <DollarSign size={20} />,
      question: "How much does roof replacement cost in Dublin, Ohio?",
      answer:
        "Roof replacement cost depends on roof size, material, and damage level. On average, homeowners in Dublin may pay between moderate and higher ranges based on material choice and labour. We provide free estimates to give accurate pricing before starting any project.",
    },
    {
      id: 7,
      icon: <Wrench size={20} />,
      question: "Do you offer emergency roof repair near me?",
      answer:
        "Yes, we provide 24/7 emergency roof repair services in Dublin, Ohio. Whether it’s storm damage, leaks, or sudden roof failure, our team responds quickly to protect your home and prevent further damage.",
    },
  ];

  return (
    <>
      <ResponsiveStyle />

      <div className="faq-section-wrap cs-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9}>
              {/* Section Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center"
              >
                {/* Subtitle pill */}
                <div className="cs-badge">
                  <span className="faq-subtitle" style={faqStyles.subtitleWrap}>
                    <span style={faqStyles.dot}></span>
                    Dublin Roofing FAQ
                  </span>
                </div>

                {/* Main heading */}
                <h2 className="cs-title cs-title-to-content" style={faqStyles.heading}>
                  Frequently Asked{" "}
                  <span style={faqStyles.headingRed}>Questions.</span>
                </h2>
              </motion.div>

              {/* FAQ Items */}
              <div className="cs-content">
                {faqs.map((faq) => {
                  const isOpen = activeId === faq.id;
                  return (
                    <motion.div
                      key={faq.id}
                      initial={false}
                      animate={{
                        backgroundColor: isOpen
                          ? "#fff"
                          : "rgba(255,255,255,0.6)",
                      }}
                      className="mb-4 rounded-4 shadow-sm overflow-hidden"
                      style={{
                        border: "1px solid #eee",
                        borderLeft: `5px solid ${isOpen ? brandRed : brandNavy}`,
                        transition: "border-left 0.3s ease",
                      }}
                    >
                      {/* Question Button */}
                      <button
                        className="faq-question-btn"
                        onClick={() => setActiveId(isOpen ? null : faq.id)}
                        style={faqStyles.btnBase}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <span
                            style={{
                              color: isOpen ? brandRed : brandNavy,
                              flexShrink: 0,
                            }}
                          >
                            {faq.icon}
                          </span>
                          <span style={faqStyles.questionText}>
                            {faq.question}
                          </span>
                        </div>
                        <motion.div
                          animate={{ rotate: isOpen ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                          style={{ flexShrink: 0, marginLeft: "12px" }}
                        >
                          <ChevronDown color={isOpen ? brandRed : "#ccc"} />
                        </motion.div>
                      </button>

                      {/* Answer */}
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                          >
                            <div
                              className="faq-answer-text"
                              style={faqStyles.answerText}
                            >
                              {faq.answer}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default Faq;
